import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatHint, MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, from, map, mergeMap, of, takeUntil } from 'rxjs';
import { Client, ClientSuggestion } from '../../../api/app-types/client.app';
import { provideSearchApi, SEARCH_API } from '../../../api/services/search.service';
import { BaseDestroy } from '../../../core/components/base-destroy/base-destroy';
import { LvrChip } from '../../../core/components/chip/chip';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';

@Component({
	selector: 'lvr-new-client-dialog',
	imports: [
		MatDialogModule,
		MatIconModule,
		MatInput,
		LvrInputDirective,
		MatFormField,
		LvrPaddingDirective,
		TranslocoPipe,
		ReactiveFormsModule,
		MatAutocompleteModule,
		MatHint,
		DatePipe,
		LvrChip,
		LvrButton,
	],
	providers: [provideSearchApi],
	templateUrl: './new-client-dialog.html',
	styleUrl: './new-client-dialog.scss',
})
export class NewClientDialog extends BaseDestroy implements OnInit {
	private readonly router = inject(Router);
	private readonly fb = inject(FormBuilder);
	private readonly searchApi = inject(SEARCH_API);
	public readonly selfRef = inject(MatDialogRef<NewClientDialog>);
	public readonly form = this.initForm();
	public readonly suggestions = signal<ClientSuggestion[]>([]);
	private readonly client = toSignal<Client | string>(
		this.form.get('clientName')!.valueChanges.pipe(
			debounceTime(300),
			map((v) => v || ''),
		),
	);
	public readonly showWarning = computed(() => this.suggestions().length > 0 && typeof this.client() === 'string');
	public readonly btnText = computed(() =>
		typeof this.client() === 'string' ? 'client.create.create' : 'client.create.select',
	);

	public ngOnInit(): void {
		this.registerFetchSuggestions();
	}

	public displayName(client?: ClientSuggestion) {
		if (!client) return '';
		return `${client.firstName} ${client.lastName}`;
	}

	public done() {
		const selected = this.client();
		if (typeof selected === 'object') {
			this.router.navigate(['/desk/client', (this.client()! as Client).id]);
			this.selfRef.close();
			return;
		}

		const rawValue = this.form.getRawValue().clientName ?? '';
		const [name, ...firstNameParts] = rawValue.trim().split(/\s+/).filter(Boolean);
		const firstName = firstNameParts.join(' ');

		this.router.navigate(['/desk/client/add'], {
			queryParams: {
				name: name ?? '',
				firstName,
			},
		});
		this.selfRef.close();
	}

	private initForm() {
		const form = this.fb.group({
			clientName: ['', [Validators.required]],
		});
		return form;
	}

	private registerFetchSuggestions() {
		this.form.valueChanges
			.pipe(
				distinctUntilChanged(), // only re-emit if changed
				takeUntil(this.destroy$), // stop emitting when destroyed
				debounceTime(300), // debounce for 300ms on user input
				map(() => this.form.get('clientName')?.value ?? ''),
				mergeMap((value) => {
					if (!value) return of();
					return from(
						this.searchApi.search({
							searchInstitutions: false,
							searchInstitutionBranches: false,
							searchClientEnvironments: false,
							searchClients: true,
							searchUsers: false,
							searchInstitutionPerson: false,
							searchSponsors: false,
							searchIso: false,
							searchTerm: value,
						}),
					);
				}),
				map((result) => {
					if (!result) return [];
					return result.entities.map(
						(item): ClientSuggestion => ({
							id: item.item.id,
							firstName: item.item.firstName,
							lastName: item.item.name,
							dateOfBirth: item.item.dateOfBirth,
							archiveId: item.item.archiveId,
						}),
					);
				}),
			)
			.subscribe((clients) => this.suggestions.set(clients));
	}
}
